'use client'

import { useState, type ReactNode } from 'react'

export interface InspectorSectionProps {
  title: string
  defaultOpen?: boolean
  children: ReactNode
  /** Optional badge rendered next to the title (counts, indicators, etc.) */
  badge?: ReactNode
}

/**
 * InspectorSection -- collapsible section wrapper used across inspectors.
 * Renders a chevron toggle, uppercase title, optional badge, and
 * conditionally-visible children.
 */
export function InspectorSection({
  title,
  defaultOpen = true,
  children,
  badge,
}: InspectorSectionProps) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="border-b border-foreground/10 last:border-b-0">
      <button
        type="button"
        className="flex items-center gap-1.5 w-full h-8 px-1 hover:bg-foreground/5 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <svg
          className={`h-3 w-3 shrink-0 text-muted-foreground transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className="text-[10px] uppercase tracking-widest font-semibold text-foreground">{title}</span>
        {badge}
      </button>
      {open && (
        <div className="pb-2.5 pt-0.5 space-y-2.5">
          {children}
        </div>
      )}
    </div>
  )
}
